<template>
  <div class="landing-container">
    <landing-header
      :properties-with-active-events="propertiesWithActiveEvents"
      :total-properties="totalProperties"
      :refresh-date-time="lastRefresh"
      :total-noise-evts="totalNoiseEvts"
      :total-tamper-evts="totalTamperEvts"
      :has-recent-events="hasRecentEvents"
      :resolved="resolved"
      @refresh-list="refreshList"
    />
    <v-container class="events-container">
      <div
        v-if="!resolved"
        style="width:100%"
      >
        <div>
          <v-skeleton-loader type="heading" />
          <v-skeleton-loader
            type="text"
            class="mt-3 mb-5"
            width="60%"
          />
        </div>
        <div
          v-for="i in 5"
          :key="i"
          class="event-panel-skeleton"
        >
          <v-skeleton-loader
            type="list-item-two-line"
            class="event-label-skeleton"
          />
          <v-skeleton-loader
            type="image"
            class="event-graph-skeleton"
          />
          <v-skeleton-loader
            type="text"
            class="event-duration-skeleton"
          />
        </div>
      </div>
      <template v-else>
        <template v-if="activeEventCount > 0 || resolvedEventCount > 0">
          <live-events-header
            :disabled="resolvedEventCount === 0"
            :resolved-visible="showResolvedEvents"
            @toggle-resolved="handleToggleResolved"
          />
          <v-expansion-panels
            v-model="expandedPanels"
            multiple
          >
            <live-events-list-item
              v-for="(item, id, idx) in eventsByProperty"
              :key="idx"
              :property-events="item.events"
              :property="item.property"
              :expanded="expandedPanels.includes(idx)"
            />
          </v-expansion-panels>
        </template>
        <template v-else>
          <div class="empty-events-list">
            <img
              :src="logo"
              width="30px"
            >
            <h1>All's quiet at your properties</h1>
            <div class="text-body">
              No events in the last 24 hours
            </div>
          </div>
        </template>
      </template>
    </v-container>
  </div>
</template>

<script>
  import axios from 'axios'
  import { mapGetters } from 'vuex'
  import { DateTime } from 'luxon'
  import LandingHeader from '@/components/landing/LandingHeader'
  import LiveEventsListItem from '@/components/landing/LiveEventsListItem.vue'
  import LiveEventsHeader from '@/components/landing/LiveEventsHeader'
  import NoiseAwareQuarterRest from '@/assets/NoiseAware_Quarter_Rest.svg'
  import { eventTypes } from '@/helpers/event-helpers'

  export default {
    name: 'LandingPage',
    components: { LandingHeader, LiveEventsListItem, LiveEventsHeader },
    data () {
      return {
        resolved: false,
        events: [],
        activeEventCount: 0,
        totalProperties: 0,
        lastRefresh: DateTime.local(),
        showResolvedEvents: true,
        expandedPanels: [],
      }
    },
    computed: {
      ...mapGetters({
        currentSelectedOrg: 'Permissions/currentSelectedOrg',
      }),
      logo () {
        return NoiseAwareQuarterRest
      },
      eventsByProperty () {
        const eventsByProperty = {}
        for (const evt of this.events) {
          if (!this.showResolvedEvents && evt.localEndDateTime) continue
          const propertyId = evt.property.id
          evt.zone = evt?.activityZone?.activityZoneName ?? 'None'
          if (eventsByProperty[propertyId]) eventsByProperty[propertyId].events.push(evt)
          else eventsByProperty[propertyId] = { events: [evt], property: evt.property }
        }
        return eventsByProperty
      },
      activeEvents () {
        return this.events.filter(evt => !evt.localEndDateTime)
      },
      propertiesWithActiveEvents () {
        return [...new Set(this.events.reduce((acc, evt) => {
          return !evt.localEndDateTime ? [...acc, evt.property.id] : acc
        }, []))].length
      },
      totalNoiseEvts () {
        return this.activeEvents.filter((evt) => evt.eventClass === eventTypes.NOISE).length
      },
      totalTamperEvts () {
        return this.activeEvents.filter(evt => evt.eventClass === eventTypes.TAMPER).length
      },
      hasRecentEvents () {
        return !!this.events.length
      },
      resolvedEventCount () {
        return this.events.reduce((acc, evt) => evt.localEndDateTime ? acc + 1 : acc, 0)
      },
    },
    watch: {
      currentSelectedOrg () {
        this.fetchList()
      },
    },
    created () {
      this.fetchList()
      this.fetchTotalProperties()
    },
    methods: {
      handleToggleResolved () {
        this.showResolvedEvents = !this.showResolvedEvents
        this.expandedPanels = []
      },
      fetchList () {
        const filter = [
          { key: 'event_type', operator: '=', value: 'EXTERNAL' },
          { key: 'start_time', operator: '>=', value: `${DateTime.utc().minus({ days: 1 }).toISO()}` },
          { key: 'event_class', operator: 'in', value: [eventTypes.NOISE, eventTypes.TAMPER].join(',') },
        ]
        const pagination = { perPage: 999, pageNum: 1 }
        if (this.currentSelectedOrg.id) filter.push({ key: 'organization_id', operator: '=', value: `${this.currentSelectedOrg.id}` })
        axios.post(`${this.$store.state.baseURL}/events/byProperty/list`, { filter, pagination })
          .then((res) => {
            const { events, activeEventCount } = res.data.data
            this.events = events ?? []
            this.activeEventCount = activeEventCount
          })
          .finally(() => {
            this.resolved = true
            this.lastRefresh = DateTime.local()
          })
      },
      refreshList () {
        this.resolved = false
        this.fetchList()
      },
      fetchTotalProperties () {
        const payload = this.currentSelectedOrg.id
          ? { filter: [{ key: 'organization_id', operator: '=', value: `${this.currentSelectedOrg.id}` }] }
          : {}
        return axios.post(`${this.$store.state.baseURL}/properties/list/count`, payload)
          .then((res) => { this.totalProperties = res.data.count })
      },
    },
  }
</script>

<style lang="scss">
.landing-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
}

.events-container {
  padding: 0 32px;
  max-width:100%;
  flex: 1 1 auto;
  display: flex;
  flex-wrap:nowrap;
  flex-direction: column;
  margin-bottom: 12px;
}
.empty-events-list {
  text-align:center;
  margin:auto;
}
.event-panel-skeleton {
  flex:none;
  width:100%;
  background-color:white;
  height:81px;
  display:flex;
  border-radius:5px;
}
.event-panel-skeleton + .event-panel-skeleton {
  margin-top: 11px;
}
.event-label-skeleton {
  flex-basis:22%;
  margin: auto 0 auto 18px;
}
.event-graph-skeleton {
  width: 70%;
  height: 60px;
  margin: auto 0;
}
.event-label-skeleton + .event-graph-skeleton {
  margin-left: 11px;
}
.event-duration-skeleton {
  width: 8%;
  margin: auto 18px;
}
</style>
